import {
  MAX_PRODUCTION_ORDER_SHEET_LINES,
  MAX_PRODUCTION_ORDER_SHEET_TEXT_LENGTH,
  type ProductionOrderSheetLine,
} from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { productionOrderSheetTotal } from '@/entities/order';
import { formatWon } from '@/shared/lib';
import { Button, Input, tableStyles } from '@/shared/ui';
import { QuantityInput } from './quantity-input';

interface LineRowsEditorProps {
  readonly lines: ProductionOrderSheetLine[];
  readonly onChange: (lines: ProductionOrderSheetLine[]) => void;
}

/** 숫자 입력 글자를 0 이상의 정수로 — 빈칸이거나 정수가 아니거나 음수면 null. */
const toInteger = (text: string): number | null => {
  if (text.trim() === '') return null;
  const parsed = Number(text);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

/** 발주서 품목 표 안 인라인 입력 — 행 추가(최대 20)·삭제(최소 1)와 행별 금액·합계. 단가 빈칸은 null 이다. */
export const LineRowsEditor = ({ lines, onChange }: LineRowsEditorProps): ReactElement => {
  const total = productionOrderSheetTotal(lines);
  const update = (index: number, patch: Partial<ProductionOrderSheetLine>): void =>
    onChange(lines.map((line, at) => (at === index ? { ...line, ...patch } : line)));

  return (
    <div className="flex flex-col gap-3">
      <div className={tableStyles.card}>
        <table className={tableStyles.table}>
          <thead className={tableStyles.head}>
            <tr>
              <th scope="col" className={tableStyles.th}>
                품목명
              </th>
              <th scope="col" className={tableStyles.th}>
                수량
              </th>
              <th scope="col" className={tableStyles.th}>
                단가
              </th>
              <th scope="col" className={tableStyles.th}>
                금액
              </th>
              <th scope="col" className={tableStyles.th}>
                <span className="sr-only">행 관리</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: 발주서 행은 id 가 없고 순서가 곧 번호다
              <tr key={index} className={tableStyles.row}>
                <td className={tableStyles.td}>
                  <Input
                    aria-label={`품목명 ${index + 1}`}
                    value={line.name}
                    maxLength={MAX_PRODUCTION_ORDER_SHEET_TEXT_LENGTH}
                    onChange={(event) => update(index, { name: event.target.value })}
                  />
                </td>
                <td className={tableStyles.td}>
                  <QuantityInput
                    label={`수량 ${index + 1}`}
                    value={line.quantity}
                    onChange={(quantity) => update(index, { quantity })}
                  />
                </td>
                <td className={tableStyles.td}>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    aria-label={`단가 ${index + 1}`}
                    className="min-w-28"
                    value={line.unitPrice ?? ''}
                    onChange={(event) =>
                      update(index, { unitPrice: toInteger(event.target.value) })
                    }
                  />
                </td>
                <td className={`${tableStyles.td} text-right whitespace-nowrap tabular-nums`}>
                  {line.unitPrice === null ? '' : formatWon(line.quantity * line.unitPrice)}
                </td>
                <td className={tableStyles.td}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={`행 삭제 ${index + 1}`}
                    disabled={lines.length <= 1}
                    onClick={() => onChange(lines.filter((_, at) => at !== index))}
                  >
                    삭제
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={tableStyles.row}>
              <th scope="row" colSpan={3} className={`${tableStyles.td} text-right font-semibold`}>
                합계 (VAT 별도)
              </th>
              <td
                className={`${tableStyles.td} text-right font-semibold whitespace-nowrap tabular-nums`}
              >
                {total === null ? '' : formatWon(total)}
              </td>
              <td className={tableStyles.td} />
            </tr>
          </tfoot>
        </table>
      </div>
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={lines.length >= MAX_PRODUCTION_ORDER_SHEET_LINES}
          onClick={() => onChange([...lines, { name: '', quantity: 1, unitPrice: null }])}
        >
          행 추가
        </Button>
      </div>
    </div>
  );
};
